import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  output,
  Output,
  SimpleChanges,
  viewChild,
  ViewChild,
} from '@angular/core';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColumnDefinition } from '../models/column-definition.interface';
import { GridDataProvider } from '../services/grid-data-provider.interface';
import { forkJoin } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { CommonModule, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CecBreakpointPipe } from '../../pipes/cec-breakpoint.pipe';
import { GridButtonTrigger } from '../models/grid-button-trigger.interface';
import { MatDivider } from '@angular/material/divider';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { DynamicColumn } from '../models/dynamic-column.interface';
import { CrossedValue } from '../models/crossed-value.interface';

@Component({
  selector: 'cec-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
  standalone: true,
  imports: [MatTableModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule, MatCardModule, DatePipe, CommonModule, CecBreakpointPipe, MatDivider],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridComponent implements AfterViewInit {
  
  /** Grid reference */
  protected grid = viewChild<MatTable<unknown> | undefined>(MatTable);

  /** Table element reference */
  protected table = viewChild.required<ElementRef>('table');

  /** Table data source reference */
  protected data: MatTableDataSource<unknown> | undefined = undefined;

  /** Table name reference */
  protected tableName = '';

  /** Table name getter reference */
  get getTableName(): string {
    return this.tableName;
  }

  /** Column definition reference */
  public columns: ColumnDefinition[] = [];

  /** Datasource reference */
  public dataSource = input<GridDataProvider | undefined>(undefined);

  /** Table identifier reference */
  public id = input<number | undefined>(undefined);

  /** Additional table identifier reference */
  public params = input<unknown | undefined>(undefined);

  /** Additional table id reference */
  public tableId = input<number | undefined>(undefined);

  /** Flag for hiding hideable columns */
  public hideColumns = input(false);

  /** Button click output emitter */
  public onButtonClicked = output<GridButtonTrigger<unknown>>();

  /** Displayed column reference */
  protected displayedColumns: string[] | undefined = undefined;

  /** Grid data reference */
  private gridData: unknown[] | undefined = undefined;

  /** Change detector reference */
  private detector: ChangeDetectorRef = inject(ChangeDetectorRef);

  /** Translation service reference */
  private translate = inject(TranslateService);

  ngAfterViewInit(): void {
    if (!this.dataSource()) {
      return;
    }

    /** obtains list of available crews and stage time by one request */
    const tableName$ = this.dataSource().getTableName(this.id(), this.params());
    const data$ = this.dataSource().getGridData(this.id(), this.params());
    const definition$ = this.dataSource().getColumnDefinition(this.id(), this.params());

    forkJoin({
      tableName: tableName$,
      data: data$,
      definition: definition$
    }).subscribe(({ tableName, data, definition }) => {
      this.tableName = tableName['name'];
      this.gridData = data;
      this.columns = definition;
      this.processColumns();
      this.data = new MatTableDataSource(data);
      
      console.log(data);

      this.detector.detectChanges();
    });
  }

  /**
   * Processes obtained column definition
   */
  private processColumns(): void {
    this.displayedColumns = this.hideColumns()
        ? this.columns.filter((x) => !x.hideAble).map((x) => x.id)
        : this.columns.map((x) => x.id);
  }

  protected onClickButton(id: string, element: unknown): void {
    this.onButtonClicked.emit({
      row: element,
      buttonId: id
    });
  }

  public reload(): void {
    this.dataSource().getGridData(this.id()).subscribe((data) => {
      this.gridData = data;
      this.data = new MatTableDataSource(data);
      this.detector.detectChanges();
    })
  }

  protected evaluateArray(value: unknown): boolean {
    return Array.isArray(value);
  }

  protected evaluateValue(value: unknown): boolean {
    if (Array.isArray(value)) {
      return !!value.length;
    }

    return value !== undefined && value !== null;
  }

  /**
   * Evaluates the cross value in cross type column
   * @param columnId the id of the desired column
   * @param values the crossed rounds array
   * @returns true/false as column evaluation result
   */
  protected evaluateCrossValue(columnId: string, values: CrossedValue[]): boolean {
    /** evaluates, if column is of type crossed */
    const column = values.find(value => value.column === columnId);
    
    /** if column does not match the reqeuirements, do nothing */
    if (!column || (column.crossed !== 'max' && column.crossed !== 'min')) {
      return false;
    }

    /** crosses the round in table */
    return true;
  }

  public exportExcel(): void {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Styled Sheet');
  
    // Prepare column headers
    const worksheetColumns: unknown[] = [];
    this.columns.forEach(col => {
      if (col.exportAble) {
        worksheetColumns.push({
          header: this.translate.instant(col.transKey),
          key: col.id
        });
      }
    });
    worksheet.columns = worksheetColumns;
  
    const headerRow = worksheet.getRow(1);
  
    // **Apply styling only to non-empty headers**
    headerRow.eachCell((cell) => {
      if (cell.value) {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF007ACC' } // Blue background for non-empty headers
        };
        cell.alignment = { horizontal: 'center' };
      }
    });
  
    // **Add Data with Per-Cell Styling**
    this.gridData.forEach(item => {
      const newRow = worksheet.addRow(
        this.columns.reduce((acc, col) => {
          if (col.exportAble) acc[col.id] = item[col.id];
          return acc;
        }, {})
      );
  
      newRow.eachCell((cell) => {
        if (cell.value) {  // **Only style filled cells**
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'D9EAD3' } // Light green fill for filled cells
          };
        }
      });
  
      newRow.alignment = { horizontal: 'center' };
    });
  
    // **Auto-adjust Column Width**
    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : '';
        maxLength = Math.max(maxLength, cellValue.length);
      });
      column.width = maxLength + 5; // Add some padding
    });
  
    // **Set Borders for All Cells**
    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });
  
    // **Save Excel File**
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'MatTable_Export.xlsx');
    });
  }
}
